import React from "react";

// Inline formatting: **bold**, *italic*, `code`, [text](url)
function inline(text, kp) {
  const nodes = [];
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|\[([^\]]+)\]\(([^)]+)\)|`([^`]+)`)/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2] !== undefined) nodes.push(<strong key={`${kp}-b${i}`} className="font-semibold text-white">{m[2]}</strong>);
    else if (m[3] !== undefined) nodes.push(<em key={`${kp}-i${i}`}>{m[3]}</em>);
    else if (m[4] !== undefined) nodes.push(<a key={`${kp}-a${i}`} href={m[5]} target="_blank" rel="noreferrer" className="text-[#ccff00] underline underline-offset-2 hover:brightness-110 break-words">{m[4]}</a>);
    else if (m[6] !== undefined) nodes.push(<code key={`${kp}-c${i}`} className="px-1.5 py-0.5 rounded bg-white/10 font-mono-cl text-[0.88em]">{m[6]}</code>);
    last = re.lastIndex; i++;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

// Parses lightweight markdown / newline-separated plain text into editorial blocks.
export function RichText({ text, className = "" }) {
  if (!text || !String(text).trim()) return null;
  const lines = String(text).replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let list = null;
  const flushList = () => { if (list) { blocks.push({ t: list.type, items: list.items.slice() }); list = null; } };

  for (const raw of lines) {
    const t = raw.trim();
    if (t === "") { flushList(); continue; }
    if (/^(---|\*\*\*|___)$/.test(t)) { flushList(); blocks.push({ t: "hr" }); continue; }
    if (t.startsWith("### ")) { flushList(); blocks.push({ t: "h3", text: t.slice(4) }); continue; }
    if (t.startsWith("## ")) { flushList(); blocks.push({ t: "h2", text: t.slice(3) }); continue; }
    if (t.startsWith("# ")) { flushList(); blocks.push({ t: "h2", text: t.slice(2) }); continue; }
    if (t.startsWith("> ")) { flushList(); blocks.push({ t: "quote", text: t.slice(2) }); continue; }
    const ol = t.match(/^\d+\.\s+(.*)$/);
    const ul = t.match(/^[-*•]\s+(.*)$/);
    if (ol) { if (!list || list.type !== "ol") { flushList(); list = { type: "ol", items: [] }; } list.items.push(ol[1]); continue; }
    if (ul) { if (!list || list.type !== "ul") { flushList(); list = { type: "ul", items: [] }; } list.items.push(ul[1]); continue; }
    flushList();
    blocks.push({ t: "p", text: t });
  }
  flushList();

  return (
    <div className={`article-prose ${className}`}>
      {blocks.map((b, i) => {
        if (b.t === "hr") return <hr key={i} className="my-8 border-white/10" />;
        if (b.t === "h2") return <h2 key={i} className="font-display text-2xl sm:text-3xl font-bold mt-10 mb-3 tracking-tight">{inline(b.text, `h2${i}`)}</h2>;
        if (b.t === "h3") return <h3 key={i} className="font-display text-xl sm:text-2xl font-bold mt-8 mb-2 tracking-tight">{inline(b.text, `h3${i}`)}</h3>;
        if (b.t === "quote") return (
          <blockquote key={i} className="my-6 border-l-2 border-[#ccff00] pl-5 italic text-zinc-200">{inline(b.text, `q${i}`)}</blockquote>
        );
        if (b.t === "ul") return (
          <ul key={i} className="my-5 space-y-2 list-none">
            {b.items.map((it, j) => (
              <li key={j} className="flex gap-3"><span className="mt-[0.6em] w-1.5 h-1.5 rounded-full bg-[#ccff00] shrink-0" /><span>{inline(it, `ul${i}-${j}`)}</span></li>
            ))}
          </ul>
        );
        if (b.t === "ol") return (
          <ol key={i} className="my-5 space-y-2 list-none counter-reset-item">
            {b.items.map((it, j) => (
              <li key={j} className="flex gap-3"><span className="font-mono-cl text-[#ccff00] text-sm shrink-0 w-5">{j + 1}.</span><span>{inline(it, `ol${i}-${j}`)}</span></li>
            ))}
          </ol>
        );
        return <p key={i} className="article-para">{inline(b.text, `p${i}`)}</p>;
      })}
    </div>
  );
}
